import AudiencePage, {
  type AudienceFaq,
  type AudienceItem,
  type AudienceStat,
} from "@/components/public/audience-page"
import type { SeoMeta } from "@/types"

const PAINS: AudienceItem[] = [
  {
    title: "A launch weekend is only as good as the marketing into it",
    description:
      "You get one chance at launch day \u2014 the reservations that weekend set the story for the whole development. Portal ads alone don't fill a sales suite. Social video ads, boosted locally, do.",
  },
  {
    title: "Every phase needs fresh creative",
    description:
      "Phase 1 marketing is old by the time phase 3 launches. Re-briefing an agency per phase is slow and expensive. QuiteRight re-renders per phase in minutes with updated pricing, housetypes and incentives baked in.",
  },
  {
    title: "CGIs look static on social",
    description:
      "Beautiful CGIs die in Instagram feed because they don't move. Wrapped in a QuiteRight video with captions, voiceover and lifestyle cues, they become scroll-stopping creative that drives sales-suite footfall.",
  },
  {
    title: "Local buyer reach beats national brand spend",
    description:
      "Most new-build buyers live within 10 miles. A \u00A3100\u2013\u00A3500 Meta boost targeting local postcodes consistently outperforms a national brand campaign for unit reservations. QuiteRight renders the creative \u2014 you run the boost.",
  },
]

const FEATURES: AudienceItem[] = [
  { title: "CGI + photo + drone hybrid", description: "Drop CGIs, real site photography and drone frames into one listing. QuiteRight sequences them into a unified social-first launch video." },
  { title: "Phase-aware rendering", description: "One development, multiple phases. Each render pulls the active housetypes, pricing and incentives \u2014 no copy-paste, no mix-ups between releases." },
  { title: "Incentive headline per render", description: "\u201CStamp duty paid,\u201D \u201C5% deposit scheme,\u201D \u201CPart-exchange\u201D \u2014 swap the incentive per render without rewriting the whole video." },
  { title: "Compliance-friendly script editor", description: "Regulated statements can be approved once and re-used. The marketing team keeps final sign-off without blocking volume." },
  { title: "Multi-site support", description: "A developer running three active sites gets three QuiteRight accounts or three listings per account \u2014 branded separately, rendered independently, reporting unified." },
  { title: "Boost-ready aspect ratios", description: "Every render ships in 9:16, 1:1 and 16:9. Ready for Meta, YouTube Shorts, LinkedIn property feeds and sales suite digital screens." },
]

const STATS: AudienceStat[] = [
  { value: "60s", label: "per phase render, pricing and incentives baked in" },
  { value: "\u00A3499", label: "per month, fully managed, unlimited renders", tone: "accent" },
  { value: "3", label: "aspect ratios from one source, no re-exporting" },
]

const FAQ: AudienceFaq[] = [
  {
    q: "Can QuiteRight render videos from CGIs only, before the site is built?",
    a: "Yes \u2014 CGIs are treated the same as photos. Upload the CGI set and the QuiteRight template sequences them with pacing, captions and voiceover tuned for phone-first viewing. Ideal for off-plan releases and pre-construction marketing.",
  },
  {
    q: "How do phased releases work?",
    a: "Each phase is either its own QuiteRight listing or the same listing with updated pricing and availability at each phase release. The render is cheap and fast \u2014 re-render whenever phase 2, 3 or 4 goes live with current housetypes and incentives.",
  },
  {
    q: "Can our marketing manager approve the creative before it's posted?",
    a: "Yes. The approval flow in QuiteRight lets the marketing team review each render before it publishes. Compliance statements and brand rules can be locked in the account settings so negotiators can't override them.",
  },
  {
    q: "Does QuiteRight connect to our CRM?",
    a: "Portal feeds (Rightmove, Zoopla, OnTheMarket) are supported natively. For CRM integrations with Reapit, Alto or site-specific new-homes CRMs, contact us \u2014 we're building integrations based on customer demand.",
  },
  {
    q: "What's the best ad spend for a launch weekend?",
    a: "For a typical 40\u201360 plot scheme, most of our developer customers run \u00A3500\u2013\u00A32,000 of Meta boost in the 10 days before launch, targeting a 10-mile radius with interest-based overlays. The creative is QuiteRight; the boost is run via the developer's existing agency or in-house.",
  },
]

export default function ForNewHomesDevelopers({ meta }: { meta: SeoMeta }) {
  return (
    <AudiencePage
      meta={meta}
      breadcrumbName="For New Homes Developers"
      heroEyebrow="For new homes developers"
      heroHeadline="Every phase, every housetype, every release \u2014 a launch video in minutes"
      heroSubhead="New-build launches are won on reservations, and reservations are won on the strength of the marketing around launch day. QuiteRight renders phase-specific social video ads \u2014 CGI, photo or drone hybrid \u2014 with pricing and incentives baked in, ready to boost locally."
      heroBadgeSmall="Fill the sales suite"
      heroBadgeLarge="every phase"
      painsHeadline="Why new homes need video built for phasing"
      pains={PAINS}
      featuresEyebrow="Built for new homes"
      featuresHeadline="Everything a developer marketing team actually needs"
      features={FEATURES}
      statsHeadline="New build video marketing, in numbers"
      stats={STATS}
      faq={FAQ}
      ctaHeadline="Phase 1, phase 5, every unit \u2014 a launch video for each"
      ctaBody="One pipeline across every development, every phase, every release \u2014 fully managed for \u00A3499 a month."
      relatedKey="for/new-homes-developers"
    />
  )
}
